/* eslint-disable react-refresh/only-export-components --
   Provider + useAuth() hook colocation is the standard React context
   pattern. */
// frontend/src/contexts/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionService, authState as authStateService, sessionApi, markSessionExpired } from '../services/SessionApiService';
import { showToast } from '../components/common/appToastBus';
import { apiErrorFrom } from '../utils/parseApiError';
import { useTranslation } from 'react-i18next';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { clearAllQueries, clearStoreScopedQueries } from '../queries/client';
// The tabbed workspace's sessionStorage layout lives in one module; logout
// sweeps it through that module rather than knowing its key shapes here.
import { clearWorkspace } from '../tabs/storage';
import SessionWarningDialog from '../components/session/SessionWarningDialog';


/**
 * Authentication Context
 * Single source of truth for authentication state across the application
 */
const AuthContext = createContext(null);

/**
 * Apply theme to DOM and cache in localStorage.
 * Backend theme_mode is the source of truth — overrides localStorage on every
 * login/init. It is non-null with a default, so every user payload carries it.
 */
const applyTheme = (mode) => {
  document.documentElement.setAttribute('data-theme', mode);
  localStorage.setItem('theme', mode);
};

/**
 * Apply sidebar collapsed state to DOM and cache in localStorage.
 * Backend sidebar_collapsed is the source of truth — overrides localStorage on every login/init.
 */
const applySidebar = (collapsed) => {
  if (collapsed) {
    document.documentElement.setAttribute('data-sidebar', 'collapsed');
  } else {
    document.documentElement.removeAttribute('data-sidebar');
  }
  localStorage.setItem('sidebarCollapsed', collapsed ? 'true' : 'false');
};

// Apply sidebar state immediately from localStorage so a collapsed sidebar does not flash open
const _initSidebar = localStorage.getItem('sidebarCollapsed');
applySidebar(_initSidebar === 'true');

/**
 * Custom hook to use authentication context  
 * Throws error if used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * True when the selected store shows merchant-facing product imagery — today
 * exactly the Shopify stores (only they populate Item.image_url / shopify_sku).
 * The one definition of the concept: item grids (via productIdentity, which
 * re-exports this) and non-grid store-type branches both call it, so "what
 * counts as image-capable" lives in a single place.
 */
export const useIsShopify = () => {
  const { selectedStore } = useAuth();
  return selectedStore?.store_type === 'shopify';
};

/**
 * What editing an item's category MEANS on the selected store:
 *
 *   { write_field: 'product_category_id' | 'item_type',
 *     creatable: boolean,      // may the merchant mint a value?
 *     scope: 'item' | 'product' }   // how far one write reaches
 *
 * Served by `core.category_editing` (backend) on every store payload, so no
 * screen infers behaviour from `category_source` — the enum answers "which
 * mode", this answers "so what do I render", and the two can't drift because
 * only one of them is a decision.
 *
 * `null` ONLY while no store is selected (auth bootstrap) — there is no store
 * whose capability could be described. A selected store that carries no
 * capability is a broken server contract, and this THROWS rather than assuming
 * one: guessing "taxonomy behaviour" would offer a category write that a
 * product-type store rejects. Both payloads that carry a store come from
 * authentication/store_payload.py.
 *
 * The category-source SWITCH lives on Store Settings, which reads
 * /auth/stores/settings/ and, while a switch runs, polls
 * /auth/stores/category-source/ for the in-flight state this hook doesn't
 * carry.
 */
export const useCategoryEditing = () => {
  const { selectedStore } = useAuth();
  if (!selectedStore) return null;
  const capability = selectedStore.category_editing;
  if (!capability?.write_field) {
    throw new Error(
      'selectedStore carries no category_editing capability — '
      + '/auth/user-context/ and /auth/stores/select/ must both send '
      + 'store_payload()',
    );
  }
  return capability;
};

/**
 * Authentication Provider Component
 * Wraps the application to provide authentication state and methods
 */
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [userContext, setUserContext] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  // The door for the shop this browser last signed in from, when the mount
  // probe answered anonymous with one (/auth/whoami/, the API's last-shop
  // cookie). App.jsx ProtectedLayout follows it instead of the login page;
  // public routes (login, setup-account, reset, confirm) never read it.
  const [shopifySignInUrl, setShopifySignInUrl] = useState(null);
  // The mount probe's failed request, while whether this browser is signed
  // in is unknown; null once it answered. retryAuth re-runs the probe.
  const [authReadError, setAuthReadError] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem('selectedStoreId');
  });

  // THIS document's view of the selected store — the basis applyUserContext
  // compares an incoming payload against. It cannot use localStorage for
  // that: localStorage is shared by every window on this origin, so a switch
  // made in another window has ALREADY written the new id there by the time
  // this window reads it. Only the first render's argument is captured, so
  // this is the boot value; applyUserContext owns every later write.
  const selectedStoreIdRef = useRef(selectedStoreId);
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);

  // Keep the latest userContext available to callbacks via a ref. The
  // user-profile update callbacks (updateTheme / updateChartTheme /
  // updateSidebar) need to read the current id / previous-value at call
  // time, but listing `userContext` as their useCallback dep would
  // re-create their identities on every state change — and userContext
  // changes often (every theme/chart/locale/store/billing/profile
  // update). Stale closures are avoided by the ref pattern; identities
  // stay stable so consumers using these callbacks in their own effect
  // deps don't re-fire.
  const userContextRef = useRef(userContext);
  useEffect(() => { userContextRef.current = userContext; }, [userContext]);

  // Reactive locale sync: any time the server-resolved effective_language
  // changes (login, refreshUser after profile save, SSO, store switch),
  // push it into i18next and <html lang>. Single source of truth —
  // callers don't need to remember to call i18n.changeLanguage themselves.
  useEffect(() => {
    const lang = userContext?.user?.effective_language;
    if (!lang) return;
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    if (document.documentElement.lang !== lang) document.documentElement.lang = lang;
  }, [userContext?.user?.effective_language, i18n]);

  // ============================================
  // APPLY USER CONTEXT — single ingestion point
  // ============================================
  // Every path that receives a fresh user-context payload (initial auth,
  // login, refreshUser, cross-tab sync) funnels through here so the derived
  // client state can never drift: React state, the persisted `selectedStoreId`
  // that builds the `X-Store-ID` request header, the stores list, and
  // theme/sidebar are all set from ONE source, so the page can never key its
  // cache to one store while the API filters another.
  const applyUserContext = useCallback((userData) => {
    setUserContext(userData);
    applyTheme(userData?.user?.theme_mode);
    applySidebar(userData.user.sidebar_collapsed);
    setStores(userData?.stores || []);

    // Lock the X-Store-ID header (localStorage) and the query-key store
    // (selectedStoreId state) to the server's selected_store. When the
    // server-resolved store changes (SSO landing, default fallback, a switch
    // in another window, an admin action), evict store-scoped queries so the
    // previous store's rows can't surface under the new store.
    //
    // "Changed" is measured against this document's own last-applied id, NOT
    // against localStorage. The switching window writes localStorage before
    // the sibling windows hear about it (SessionApiService.selectStore), so a
    // sibling comparing against localStorage would see "same store, nothing to
    // do" and keep serving the previous store's rows while its X-Store-ID
    // header already asks the API for the new one.
    //
    // The header is written UNCONDITIONALLY, not only on change: the ref is
    // seeded from localStorage at mount, and the `?sso=1` reset in
    // initializeAuth clears localStorage AFTER that seed — so a Shopify
    // sign-in landing on the store this browser last used arrives with a
    // matching ref and an empty header, and its first request would go out
    // without X-Store-ID. Only the eviction is gated on change; the mirror to
    // localStorage always happens.
    const nextStoreId = userData?.selected_store?.id ?? null;
    const prevStoreId = selectedStoreIdRef.current;
    if (nextStoreId) localStorage.setItem('selectedStoreId', nextStoreId);
    else localStorage.removeItem('selectedStoreId');
    if (nextStoreId !== prevStoreId) {
      selectedStoreIdRef.current = nextStoreId;
      if (prevStoreId) clearStoreScopedQueries();
      setSelectedStoreId(nextStoreId);
    }
  }, []);

  // ============================================
  // INITIALIZATION
  // ============================================
  useEffect(() => {
    // initializeAuth declared later in same scope; closure-captured at first render. Hoisting is the proper fix; flagged as a separate refactor.
    // eslint-disable-next-line react-hooks/immutability
    initializeAuth();
    // Mount-only auth init — initializeAuth is defined below in the
    // provider body and is re-created every render; including it in
    // deps would re-fire SSO + session checks on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeAuth = async () => {
    try {
      // The Shopify OAuth callback lands here with ?sso=1 (backend
      // shopify_app/views/oauth.py): this tab may still hold a previous
      // person's cached identity, so every cache is cleared before the probe.
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('sso') === '1') {
        authStateService.clearAll();

        // Remove sso param from URL to prevent re-clearing on refresh
        urlParams.delete('sso');
        const newSearch = urlParams.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, '', newUrl);
      }

      // Probe auth state with a lightweight always-200 endpoint. Avoids
      // generating `Unauthorized: /auth/user-context/` warnings for every
      // anonymous visitor landing on /login.
      const probe = await sessionService.whoami();

      if (probe.authenticated) {
        // Fetch user context and apply it through the single ingestion point
        // (state + header store + stores list + theme/sidebar in lockstep).
        const userData = await authStateService.getCurrentUser();
        applyUserContext(userData);
      } else {
        setShopifySignInUrl(probe.shopify_sign_in_url);
      }
    } catch (error) {
      // No answer is not "anonymous": a deploy restarting the API or a
      // dropped connection would otherwise send a signed-in merchant to the
      // login page. ProtectedLayout shows a retry screen instead.
      console.error('Failed to initialize authentication:', error);
      setAuthReadError(error);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const retryAuth = () => {
    setAuthReadError(null);
    setIsLoading(true);
    initializeAuth();
  };

  // ============================================
  // LOGIN METHOD
  // ============================================
  // Signs in and lands the session, or throws. It does not catch: the refusal
  // carries the status the sign-in page turns into the merchant's message.
  const login = useCallback(async (credentials) => {
    setIsLoading(true);
    try {
      await sessionService.login(credentials);

      // Fetch fresh user context and apply it through the single ingestion
      // point (state + header store + stores list + theme/sidebar).
      const userData = await authStateService.getCurrentUser();
      applyUserContext(userData);

      // Navigate to home page
      navigate('/', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, applyUserContext]);

  // ============================================
  // LOGOUT METHOD
  // ============================================
  // Everything this browser holds about the session that is ending. One
  // function, called from both the success and the failure path, because a
  // logout that fails server-side must still leave nothing behind locally —
  // and because a cleanup step added to only one of two copies is how a
  // leak survives.
  const clearClientSession = useCallback(() => {
    setUserContext(null);
    // The server deleted the last-shop cookie with the session: a signed-out
    // browser meets the login page, not the door.
    setShopifySignInUrl(null);
    // Auth caches, the tenant cache and the store selection.
    authStateService.clearAll();
    // TanStack's query cache — reference data (users, suppliers, calendars,
    // ...) and every TanStack-managed query in the app. Without this, the
    // next user logging in would see the previous user's tenant data served
    // from cache.
    clearAllQueries();
    // The tabbed workspace: open tabs, the focused tab, and each tab's
    // filter selections and grid state. Same rule as the query cache — this
    // is sessionStorage, and signing in again does not replace the document,
    // so without the sweep the next user restores the previous user's tabs.
    clearWorkspace();
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await sessionService.logout();
      clearClientSession();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      clearClientSession();
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, clearClientSession]);

  // ============================================
  // SESSION TIMEOUT HANDLER
  // ============================================
  const handleSessionTimeout = useCallback(async () => {
    markSessionExpired('expired');
    await logout();
  }, [logout]);

  // ============================================
  // SESSION TIMEOUT HOOK (with userContext for "remember me" detection)
  // ============================================
  const { showWarning, timeLeft, resetTimer, notifyApiActivity } = useSessionTimeout(
    !!userContext,
    handleSessionTimeout,
    userContext  // ← Pass userContext so hook can check is_remember_me
  );

  // ============================================
  // STAY LOGGED IN HANDLER
  // ============================================
  const handleStayLoggedIn = async () => {
    try {
      await sessionApi.post('/auth/keepalive/');
      resetTimer();
    } catch (error) {
      console.error('Keepalive failed:', error);
      showToast(t('session.keepaliveFailed'), 'warning');
    }
  };

  // ============================================
  // REFRESH USER CONTEXT
  // ============================================
  const refreshUser = useCallback(async () => {
    try {
      // Clear cache to force fresh fetch
      authStateService.clearCache();
      
      // Fetch fresh user context
      const userData = await authStateService.getCurrentUser();
      
      // CRITICAL: Preload avatar before updating state
      // This prevents the "alt text flash" when rendering
      if (userData?.user?.avatar) {
        await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve();
          };
          img.onerror = () => {
            console.warn('Avatar preload failed, continuing anyway');
            resolve(); // Continue even if preload fails
          };
          // Set src to start loading
          img.src = userData.user.avatar;
          
          // Timeout after 3 seconds to prevent hanging
          setTimeout(() => {
            console.warn('Avatar preload timeout');
            resolve();
          }, 3000);
        });
      }
      
      // Now apply through the single ingestion point — avatar is in browser
      // cache. This also re-syncs the X-Store-ID header store (localStorage)
      // to the server's selected_store.
      applyUserContext(userData);

      return userData;
    } catch (error) {
      console.error('Failed to refresh user context:', error);
      throw error;
    }
  }, [applyUserContext]);

  // ============================================
  // UPDATE AVATAR
  // ============================================
  const updateAvatar = useCallback(async (file) => {
    try {
      // Upload avatar
      const response = await sessionService.uploadAvatar(file);
      
      // Refresh user context to get new avatar URL
      await refreshUser();
      
      return { success: true, avatar: response.avatar };
    } catch (error) {
      console.error('Failed to update avatar:', error);
      throw error;
    }
  }, [refreshUser]);

  // ============================================
  // DELETE AVATAR
  // ============================================
  const deleteAvatar = useCallback(async () => {
    try {
      // Delete avatar
      await sessionService.deleteAvatar();
      
      // Refresh user context to clear avatar URL
      await refreshUser();
      
      return { success: true };
    } catch (error) {
      console.error('Failed to delete avatar:', error);
      throw error;
    }
  }, [refreshUser]);

  // ============================================
  // PERSIST USER FIELDS
  // ============================================
  // PATCHes profile fields and, once the server accepted them, merges exactly
  // those fields into the user-context user. The PATCH answers with the
  // profile payload, which is not the user-context shape: it has no
  // `is_staff`, `has_password` or `effective_*` — those resolve against the
  // selected store, which only /auth/user-context/ knows. So the answer never
  // replaces the user.
  const persistUserFields = useCallback(async (fields) => {
    const response = await sessionApi.patch('/auth/user-profile/me/', fields);
    if (!response.ok) throw await apiErrorFrom(response, 'PATCH /auth/user-profile/me/');
    authStateService.clearCache();
    setUserContext(prev => ({ ...prev, user: { ...prev?.user, ...fields } }));
  }, []);

  // ============================================
  // UPDATE THEME
  // ============================================
  const updateTheme = useCallback(async (newTheme) => {
    // Roll back to what the merchant saw before this click, as updateSidebar
    // does: userContext only catches up when a PATCH answers.
    const previousTheme = document.documentElement.getAttribute('data-theme');
    // applyTheme() mutates <html data-theme=…> synchronously. The toggle
    // button's icon-swap CSS keys off [data-theme="…"] selectors directly,
    // so the animation fires in the same paint as the click — no React
    // state involvement, no optimistic update needed. Components that DO
    // read userContext.user.theme_mode for non-animated UI catch up once the
    // PATCH is accepted; a brief text-label lag isn't visible the way an
    // animation lag is.
    applyTheme(newTheme);

    try {
      await persistUserFields({ theme_mode: newTheme });
    } catch (error) {
      console.error('Failed to persist theme:', error);
      applyTheme(previousTheme);
      throw error;
    }
  }, [persistUserFields]);

  // ============================================
  // UPDATE CHART THEME
  // ============================================
  const updateChartTheme = useCallback(async (newChartTheme) => {
    const ctx = userContextRef.current;
    const previousChartTheme = ctx?.user?.chart_theme;
    // Optimistic local update
    setUserContext(prev => ({
      ...prev,
      user: { ...prev?.user, chart_theme: newChartTheme },
    }));

    try {
      if (ctx?.user?.id) {
        await persistUserFields({ chart_theme: newChartTheme });
      }
    } catch (error) {
      console.error('Failed to persist chart theme:', error);
      setUserContext(prev => ({
        ...prev,
        user: { ...prev?.user, chart_theme: previousChartTheme },
      }));
      throw error;
    }
  }, [persistUserFields]);

  // ============================================
  // UPDATE SIDEBAR STATE
  // ============================================
  const updateSidebar = useCallback(async (collapsed) => {
    const ctx = userContextRef.current;
    // Roll back to what the merchant saw before this click. userContext only
    // catches up when a PATCH answers, so after a fast second click it still
    // holds the state from before the first one.
    const previousCollapsed = document.documentElement.getAttribute('data-sidebar') === 'collapsed';
    applySidebar(collapsed);

    try {
      if (ctx?.user?.id) {
        await persistUserFields({ sidebar_collapsed: collapsed });
      }
    } catch (error) {
      console.error('Failed to persist sidebar state:', error);
      applySidebar(previousCollapsed);
      throw error;
    }
  }, [persistUserFields]);

  // ============================================
  // UPDATE UI PREFERENCES (generic, merge-based)
  // ============================================
  // Mirror the latest prefs in a ref so `updatePreference` can read the
  // current value synchronously. This matters when a consumer issues two
  // calls in a single event tick (e.g. `setFilterA(x); setFilterB(y)`
  // where both write nested keys under the same parent preference): the
  // first call mutates the ref, so the second call's updater sees A's
  // write and merges B on top of it. Without this, both calls would read
  // the pre-event prefs and the second would clobber the first on the
  // server.
  const prefsRef = useRef({});
  useEffect(() => {
    prefsRef.current = userContext?.user?.ui_preferences || {};
  }, [userContext?.user?.ui_preferences]);

  const userIdRef = useRef(null);
  useEffect(() => {
    userIdRef.current = userContext?.user?.id ?? null;
  }, [userContext?.user?.id]);

  // Accepts either a plain value (`updatePreference('theme', 'dark')`) or
  // a functional updater (`updatePreference('chart_preferences', prev =>
  // ({...prev, [id]: ...}))`). Use the functional form whenever the new
  // value depends on the current one — it reads from `prefsRef`, which
  // is always the latest committed state, so two synchronous calls don't
  // race on a stale closure.
  const updatePreference = useCallback(async (key, valueOrUpdater) => {
    const previousValue = prefsRef.current?.[key];
    const nextValue = typeof valueOrUpdater === 'function'
      ? valueOrUpdater(previousValue)
      : valueOrUpdater;

    // Mutate ref immediately so any follow-up call in the same tick sees
    // this write. React state update below drives the re-render.
    prefsRef.current = { ...prefsRef.current, [key]: nextValue };

    setUserContext(prev => ({
      ...prev,
      user: {
        ...prev?.user,
        ui_preferences: { ...(prev?.user?.ui_preferences || {}), [key]: nextValue },
      },
    }));

    try {
      if (userIdRef.current) {
        await sessionApi.patch('/auth/user-profile/me/', {
          ui_preferences: { [key]: nextValue },
        });
        authStateService.clearCache();
      }
    } catch (error) {
      console.error('Failed to persist preference:', error);
      // Rollback both ref and state so subsequent calls don't build on
      // the un-persisted optimistic value.
      prefsRef.current = { ...prefsRef.current, [key]: previousValue };
      setUserContext(prev => ({
        ...prev,
        user: {
          ...prev?.user,
          ui_preferences: { ...(prev?.user?.ui_preferences || {}), [key]: previousValue },
        },
      }));
    }
  }, []);

  // ============================================
  // STORE MANAGEMENT
  // ============================================

  /**
   * Fetch stores for the current user's tenant
   */
  const fetchStores = useCallback(async () => {
    setStoresLoading(true);
    try {
      const storesData = await sessionService.getStores();
      setStores(storesData.stores || []);
      return storesData.stores || [];
    } catch (error) {
      console.error('Failed to fetch stores:', error);
      setStores([]);
      throw error;
    } finally {
      setStoresLoading(false);
    }
  }, []);

  /**
   * Switch the session to another store.
   *
   * A switch is an application RESTART, not an in-place state update: every
   * query key, grid and tab slot in the tree is scoped to one store, so the
   * document is replaced onto the new selection.
   *
   * Nothing here touches React state, deliberately. Replacing the document
   * only STARTS the navigation — the live tree keeps rendering until the new
   * document arrives, so pushing the new store into `userContext` first would
   * re-fire every store-scoped effect against a document already being torn
   * down, and every such request dies at unload. The reads that fail closed
   * (`hooks/useEntryStatus`, PricingGate) also decline to record a request
   * the browser killed on the way out (`utils/pageTeardown`), which covers a
   * teardown this module does not initiate, such as an F5.
   *
   * `prepareRestart` runs once the server has committed the selection and
   * before the navigation — the seam for teardown that must be persisted first
   * (useStoreSwitch closes the non-Home tabs there) and must NOT happen when the
   * switch failed. It returns the path the new document loads: the OLD url is
   * not a valid destination, because it addresses the store the session just
   * left. Failures propagate to the caller; nothing is navigated.
   *
   * `replace`, not `assign`: the previous store's url must not sit in history
   * one Back press away from the new store's session.
   */
  const switchStore = useCallback(async (storeId, prepareRestart) => {
    await sessionService.selectStore(storeId);
    // The user-context cache lives in sessionStorage and SURVIVES the restart —
    // without this the next getCurrentUser() re-seeds the previous store. The
    // TanStack cache is in-memory and goes with the document, so no query
    // eviction is needed here (applyUserContext still evicts on the cross-tab
    // convergence path, which has no restart).
    authStateService.clearCache();
    window.location.replace(prepareRestart());
  }, []);

  // Get selected store from userContext (set by backend based on SSO or default)
  const selectedStore = userContext?.selected_store || null;

  // ============================================
  // SESSION IDENTITY GUARD + STALE DATA REFRESH
  // Detects when another tab logs in as a different user
  // (shared session cookie changes identity behind our back).
  // Also refreshes userContext when tab regains focus after 5+ min
  // to pick up changes made elsewhere (timezone, stores, roles).
  // ============================================
  const currentUserIdRef = useRef(null);
  // Always-fresh ref: cross-tab login detection reads this from a
  // 'storage' event listener; the listener is set up once and needs the
  // latest user id without re-binding.
  // eslint-disable-next-line react-hooks/refs
  currentUserIdRef.current = userContext?.user?.id || null;
  // Date.now() in useRef initializer is evaluated every render but only
  // the first render's value is captured. The purity rule errs on the
  // side of catching impure expressions in render path even when their
  // result is discarded. Pattern is correct.
  // eslint-disable-next-line react-hooks/purity
  const lastRefreshRef = useRef(Date.now());

  useEffect(() => {
    const REFRESH_THROTTLE_MS = 5 * 60 * 1000; // 5 minutes

    const handleVisibilityChange = async () => {
      if (document.hidden || !currentUserIdRef.current) return;

      try {
        const freshData = await sessionService.getUserContext();
        const freshUserId = freshData?.user?.id;

        if (freshUserId && freshUserId !== currentUserIdRef.current) {
          console.warn('Session identity changed — logging out');
          await logout();
          return;
        }

        // Refresh full context if enough time has passed (picks up
        // timezone, store list, role changes made in other tabs/devices)
        if (Date.now() - lastRefreshRef.current > REFRESH_THROTTLE_MS) {
          lastRefreshRef.current = Date.now();
          await refreshUser();
        }
      } catch (error) {
        // Only the server saying the session is gone ends it. A failed
        // check (API restarting for a deploy, connection dropped) leaves the
        // merchant signed in.
        if (error.status === 401 || error.status === 403) {
          console.warn('Session lost while tab was hidden — logging out');
          await logout();
          return;
        }
        console.error('Session check on tab focus failed:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [logout, refreshUser]);

  // ============================================
  // CROSS-TAB STORE SYNC
  // The selected store lives in localStorage (shared across this origin's
  // tabs) because the non-React API layer reads it to build the X-Store-ID
  // header. When another tab switches store, this tab must converge —
  // otherwise it keeps querying the previous store. A 'storage' event never
  // fires in the tab that made the change, so any event here is another tab's
  // switch. The session cookie is shared, so refreshUser() pulls the
  // authoritative selection and applyUserContext re-syncs state + header and
  // evicts the previous store's cached queries.
  //
  // The workspace converges too: `selectedStoreId` changing under a live
  // document is what CloseTabsOnStoreChange (tabs/) reacts to, closing the
  // tabs the previous store opened. That reaction keys off the state, not off
  // this event, so it covers every way the store can change beneath a live
  // document — not just a sibling window's switch.
  // ============================================
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'selectedStoreId') return;
      if (!currentUserIdRef.current) return; // not authenticated — ignore
      // Session-gone (cross-tab logout) is handled by the visibility/timeout
      // paths; here we only converge on a store switch.
      refreshUser().catch(() => {});
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshUser]);

  // ============================================
  // CONTEXT VALUE
  // ============================================
  const value = {
    // State
    userContext,
    user: userContext?.user || null,
    organization: userContext?.organization || null,
    session: userContext?.session || null,
    isAuthenticated: !!userContext,
    isLoading,
    isInitialized,
    shopifySignInUrl,
    authReadError,
    retryAuth,

    // Store context (for store-scoped API filtering)
    selectedStoreId,
    selectedStore,
    stores,
    storesLoading,

    // Deliberately no viewer timezone. A merchant-facing calendar date takes
    // the STORE's zone (`selectedStore.timezone` → useStoreToday, which
    // returns '' rather than substituting); rendering an instant is the only
    // legitimate use of a viewer clock, and formatters `_timezoneOf` owns it.

    // Methods
    login,
    logout,
    refreshUser,
    updateAvatar,
    deleteAvatar,
    updateTheme,
    updateChartTheme,
    updateSidebar,
    updatePreference,
    notifyApiActivity, // Session timeout API activity tracking

    // Store methods
    fetchStores,
    switchStore,
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Session Warning Dialog - Only shown for non-remember-me sessions */}
      <SessionWarningDialog
        open={showWarning}
        timeLeft={timeLeft}
        onStayLoggedIn={handleStayLoggedIn}
      />
      
    </AuthContext.Provider>
  );
};

export default AuthContext;